import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { coursesAPI, roundsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../utils/constants';

export default function CreateEvent() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [eventName, setEventName] = useState('');
  const [eventType, setEventType] = useState('tml'); // 'tml' | 'regular' | 'open'
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [numberOfRounds, setNumberOfRounds] = useState(3);
  const [roundDates, setRoundDates] = useState(['', '', '']);
  // Per-round course overrides, indexed by round. Null = use the event course.
  // Only surfaced when "different course per round" is ticked.
  const [perRoundCourses, setPerRoundCourses] = useState(false);
  const [roundCourseIds, setRoundCourseIds] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    // Update round dates array when number of rounds changes
    const newDates = Array(numberOfRounds).fill('').map((_, i) => roundDates[i] || '');
    setRoundDates(newDates);
    setRoundCourseIds(prev => Array(numberOfRounds).fill(null).map((_, i) => prev[i] ?? null));
  }, [numberOfRounds]);

  // The course actually used for a given round index
  const courseForRound = (index) =>
    (perRoundCourses ? roundCourseIds[index] : null) ?? selectedCourseId;

  const handleRoundCourseChange = (index, courseId) => {
    setRoundCourseIds(prev => {
      const next = [...prev];
      next[index] = courseId;
      return next;
    });
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const coursesData = await coursesAPI.getAll();
      setCourses(coursesData.courses || []);
    } catch (err) {
      setError('Failed to load courses: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (index, value) => {
    const newDates = [...roundDates];
    newDates[index] = value;
    setRoundDates(newDates);
  };

  const handleCreateEvent = async () => {
    setError('');

    // Validation
    if (!eventName.trim()) {
      setError('Please enter an event name');
      return;
    }

    if (!selectedCourseId) {
      setError('Please select a course');
      return;
    }

    try {
      setCreating(true);

      // Create rounds without players
      // TML events:     first round is practice, rest are championship
      // Open (guest):   every round is championship so they all sum to a total
      // Regular events: all rounds are standard (no aggregation)
      const promises = [];
      for (let i = 0; i < numberOfRounds; i++) {
        let roundType;
        if (eventType === 'tml') {
          roundType = i === 0 ? 'practice' : 'championship';
        } else if (eventType === 'open') {
          roundType = 'championship';
        } else {
          roundType = 'standard';
        }

        const roundData = {
          courseId: courseForRound(i),
          eventName: eventName.trim(),
          eventType: eventType,
          roundNumber: i + 1,
          startDate: roundDates[i] || null,
          roundType: roundType
        };

        promises.push(roundsAPI.createEmptyRound(roundData));
      }

      await Promise.all(promises);

      alert(`Event "${eventName}" created successfully with ${numberOfRounds} rounds!`);
      navigate(ROUTES.EVENTS);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Create Event</h1>
        <Card>
          <p className="text-lg text-error">
            You must be logged in as admin to create events.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 pb-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Event</h1>
      <p className="text-base text-gray-600 mb-6">
        Create a new golf event with multiple rounds. Players and groups will be assigned before each round starts.
      </p>

      {error && (
        <div className="bg-red-100 text-red-800 px-6 py-4 border border-red-300 mb-6 rounded-lg">
          {error}
        </div>
      )}

      {/* Event Name */}
      <Card title="Event Details" className="mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Event Type *
            </label>
            <select
              value={eventType}
              onChange={(e) => {
                const next = e.target.value;
                setEventType(next);
                // Guest events are typically two rounds summed to a winner
                if (next === 'open') setNumberOfRounds(2);
              }}
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="tml">TML Championship (Practice + Championship Rounds)</option>
              <option value="regular">Regular Event (Standard Rounds)</option>
              <option value="open">Guest Event — The Clubhouse (Cumulative, unbranded)</option>
            </select>
            <p className="text-sm text-gray-600 mt-2">
              {eventType === 'tml'
                ? 'TML events have cumulative scoring across championship rounds. First round is practice, remaining rounds are championship.'
                : eventType === 'open'
                ? 'For an outside group hosted on the app. Every round counts and totals sum to decide the winner — same as TML — but with no TML branding, no Bowl, and no entry in Society history. Lives at /clubhouse.'
                : 'Regular events have independent rounds without score aggregation.'
              }
            </p>
          </div>

          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Event Name *
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder={
                eventType === 'tml' ? 'e.g., TML 26'
                : eventType === 'open' ? 'e.g., Clubhouse Open 2026'
                : 'e.g., Summer Classic 2026'
              }
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Number of Rounds *
            </label>
            <select
              value={numberOfRounds}
              onChange={(e) => setNumberOfRounds(parseInt(e.target.value))}
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value={1}>1 Round</option>
              <option value={2}>2 Rounds{eventType === 'open' ? ' (both count)' : ''}</option>
              <option value={3}>3 Rounds{eventType === 'tml' ? ' (TML Standard)' : ''}</option>
              <option value={4}>4 Rounds</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Course Selection */}
      <Card title={perRoundCourses ? 'Default Course' : 'Select Course'} className="mb-6">
        {courses.length === 0 ? (
          <p className="text-gray-600">No courses available. Please create a course first.</p>
        ) : (
          <div className="space-y-3">
            {courses.map((course) => (
              <button
                key={course.id}
                onClick={() => setSelectedCourseId(course.id)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  selectedCourseId === course.id
                    ? 'border-primary bg-primary bg-opacity-10'
                    : 'border-gray-200 hover:border-primary'
                }`}
              >
                <div className="font-bold text-lg text-gray-900">{course.name}</div>
                {course.location && (
                  <div className="text-sm text-gray-600">{course.location}</div>
                )}
              </button>
            ))}
          </div>
        )}

        {numberOfRounds > 1 && courses.length > 1 && (
          <label className="flex items-start gap-3 mt-4 pt-4 border-t border-gray-200 cursor-pointer">
            <input
              type="checkbox"
              checked={perRoundCourses}
              onChange={(e) => setPerRoundCourses(e.target.checked)}
              className="mt-1 w-5 h-5"
            />
            <span>
              <span className="block text-base font-semibold text-gray-700">
                Rounds are played at different courses
              </span>
              <span className="block text-sm text-gray-600">
                Pick a course per round below. Stableford points still sum across
                rounds — each round is scored against its own pars and stroke indexes.
              </span>
            </span>
          </label>
        )}
      </Card>

      {/* Per-Round Courses */}
      {perRoundCourses && selectedCourseId && (
        <Card title="Course Per Round" className="mb-6">
          <div className="space-y-3">
            {Array(numberOfRounds).fill(0).map((_, index) => (
              <div key={index}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Round {index + 1}
                </label>
                <select
                  value={roundCourseIds[index] ?? selectedCourseId}
                  onChange={(e) => handleRoundCourseChange(index, parseInt(e.target.value))}
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}{course.location ? ` — ${course.location}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Round Dates (Optional) */}
      {selectedCourseId && (
        <Card title="Round Dates (Optional)" className="mb-6">
          <p className="text-sm text-gray-600 mb-4">
            You can set dates for each round, or leave blank to set later.
          </p>
          <div className="space-y-3">
            {Array(numberOfRounds).fill(0).map((_, index) => (
              <div key={index}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Round {index + 1}
                </label>
                <input
                  type="date"
                  value={roundDates[index]}
                  onChange={(e) => handleDateChange(index, e.target.value)}
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Review & Create */}
      {eventName && selectedCourseId && (
        <Card title="Review & Create" className="mt-6">
          <div className="space-y-3 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-700">Event Type:</p>
              <p className="text-base text-gray-900">
                {eventType === 'tml' ? 'TML Championship'
                  : eventType === 'open' ? 'Guest Event — The Clubhouse'
                  : 'Regular Event'}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Event Name:</p>
              <p className="text-base text-gray-900">{eventName}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">
                {perRoundCourses ? 'Courses:' : 'Course:'}
              </p>
              {perRoundCourses ? (
                <ul className="text-base text-gray-900">
                  {Array(numberOfRounds).fill(0).map((_, index) => (
                    <li key={index}>
                      Round {index + 1}: {courses.find(c => c.id === courseForRound(index))?.name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base text-gray-900">
                  {courses.find(c => c.id === selectedCourseId)?.name}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Rounds:</p>
              <p className="text-base text-gray-900">
                {numberOfRounds} {numberOfRounds === 1 ? 'round' : 'rounds'}
                {eventType === 'tml' && numberOfRounds > 1 && ` (1 practice, ${numberOfRounds - 1} championship)`}
                {eventType === 'open' && numberOfRounds > 1 && ' (all count towards the total)'}
              </p>
            </div>
            {eventType === 'open' && (
              <div className="bg-ch-bg border border-ch-secondary/20 rounded-lg p-3">
                <p className="text-sm text-ch-primary">
                  Guests will follow this at <span className="font-semibold">/clubhouse</span>.
                  Assign players to Round 2 only after Round 1 is finished and handicaps
                  are updated — handicaps are locked in at the moment players are assigned.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4">
            <Button
              variant="secondary"
              onClick={() => navigate(ROUTES.HISTORY)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateEvent}
              disabled={creating}
            >
              {creating ? 'Creating Event...' : 'Create Event'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
